import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import CsDiagnostics from '../../../models/CsDiagnostics';
import { postSummaryEndpoint } from '../../../api/csDiagnosticsApi';
import beautifyColumnKey from '../utils/beautifyColumnKey';
import ExpandablePanel from './ExpandablePanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TableExportButtons, useTableExport } from '../../../../components/common/table-export';
import { buildDiagnosticsExportFilename, getSelectedCsDeviceId } from '../../../../utils/export/diagnosticsExportFilename.js';

const MAX_VISIBLE_ROWS = 150;
const DEBOUNCE_MS = 250;

const identityResolveHeaders = (rawKeys) => rawKeys;

export default function DiagnosticsDynamicTablePanel({
  title,
  endpoint,
  sectionId,
  csDiagnosticsParams,
  setIsLoading,
  emptyWarning = 'Data not found or empty.',
  fixedHeight = 420,
  exportSlug,
  getExportFilename,
  exportTableKey,
  resolveHeaders = identityResolveHeaders,
  resolveStickyColumnKey,
}) {
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const cellRefs = useRef({});
  const [inputValues, setInputValues] = useState({});
  const [debouncedFilter, setDebouncedFilter] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [matches, setMatches] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    setRows([]);
    setHeaders([]);
    setInputValues({});
    setDebouncedFilter({});
    setSearchValue('');
    setMatches([]);
    setCurrentMatchIndex(-1);
    cellRefs.current = {};
  }, [csDiagnosticsParams]);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(inputValues);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [inputValues]);

  const filteredData = useMemo(
    () =>
      rows?.filter((row) =>
        headers.every((key) => {
          const filter = debouncedFilter[key];
          if (!filter) return true;
          const cell = row[key];
          const cellStr = cell !== null && cell !== undefined ? cell.toString().toLowerCase() : 'null';
          return cellStr.includes(filter);
        })
      ) ?? [],
    [rows, headers, debouncedFilter]
  );

  const visibleRows = useMemo(
    () => filteredData.slice(0, MAX_VISIBLE_ROWS),
    [filteredData]
  );

  const headerLabels = useMemo(() => headers.map((key) => beautifyColumnKey(key)), [headers]);

  const stickyColumnKey = useMemo(
    () => (resolveStickyColumnKey ? resolveStickyColumnKey(headers) : undefined),
    [headers, resolveStickyColumnKey]
  );

  const isStickyColumn = (key) => stickyColumnKey != null && key === stickyColumnKey;

  const getExportFilenameBuilt = useCallback(
    (ext) =>
      buildDiagnosticsExportFilename({
        tableKey: exportTableKey,
        csId: getSelectedCsDeviceId(csDiagnosticsParams),
        fromDate: csDiagnosticsParams?.start_date,
        toDate: csDiagnosticsParams?.end_date,
        extension: ext,
      }),
    [exportTableKey, csDiagnosticsParams]
  );

  const effectiveGetExportFilename = getExportFilename ?? (exportTableKey ? getExportFilenameBuilt : undefined);

  const getExportRows = useCallback(
    (scope) => {
      if (scope === 'current-page') return visibleRows;
      if (scope === 'filtered') return filteredData;
      return rows;
    },
    [visibleRows, filteredData, rows]
  );

  const getExportScopeCounts = useCallback(
    () => ({
      currentPage: visibleRows.length,
      filtered: filteredData.length,
      all: rows.length,
    }),
    [visibleRows.length, filteredData.length, rows.length]
  );

  const { exportCsv, exportExcel, getResolvedExportFilename, getExportScopeCounts: resolveScopeCounts, isExporting, canExport } = useTableExport({
    exportSlug,
    getExportFilename: effectiveGetExportFilename,
    columnKeys: headers,
    headerLabels,
    getExportRows,
    getExportScopeCounts,
    sheetTitle: title,
    emptyDisplay: '- -',
  });

  const handleSearchChange = (e, key) => {
    setInputValues((prev) => ({
      ...prev,
      [key]: e.target.value.toLowerCase(),
    }));
  };

  const performSearch = (val) => {
    if (!val.trim()) {
      setMatches([]);
      setCurrentMatchIndex(-1);
      visibleRows.forEach((row, rowIndex) => {
        headers.forEach((key, colIndex) => {
          const targetCell = cellRefs.current[`${sectionId}-${rowIndex}-${colIndex}`];
          if (targetCell) {
            targetCell.innerText = row[key] || '- -';
          }
        });
      });
      return;
    }

    const newMatches = [];
    const value = val.toLowerCase();
    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedValue, 'gi');

    visibleRows.forEach((row, rowIndex) => {
      headers.forEach((key, colIndex) => {
        const cellValue = row[key]?.toString() || '';
        const targetCell = cellRefs.current[`${sectionId}-${rowIndex}-${colIndex}`];
        if (targetCell) {
          if (cellValue.toLowerCase().includes(value)) {
            const highlightedHtml = cellValue.replace(regex, (match) => `<span class="bg-warning text-dark">${match}</span>`);
            targetCell.innerHTML = highlightedHtml;
            const cellMatches = [...targetCell.querySelectorAll('span.bg-warning')];
            cellMatches.forEach((match) => newMatches.push(match));
          } else {
            targetCell.innerText = cellValue || '- -';
          }
        }
      });
    });

    setMatches(newMatches);
    if (newMatches.length > 0) {
      setCurrentMatchIndex(0);
      newMatches.forEach((match, index) => {
        match.classList.toggle('bg-danger', index === 0);
        match.classList.toggle('text-white', index === 0);
      });
      newMatches[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    } else {
      setCurrentMatchIndex(-1);
    }
  };

  const navigateMatch = (direction) => {
    if (matches.length === 0) return;
    let newIndex = direction === 'next' ? currentMatchIndex + 1 : currentMatchIndex - 1;
    if (newIndex >= matches.length) newIndex = 0;
    if (newIndex < 0) newIndex = matches.length - 1;

    setCurrentMatchIndex(newIndex);
    matches.forEach((match, index) => {
      const isCurrent = index === newIndex;
      match.classList.toggle('bg-danger', isCurrent);
      match.classList.toggle('text-white', isCurrent);
      match.classList.toggle('bg-warning', !isCurrent);
      match.classList.toggle('text-dark', !isCurrent);
    });
    matches[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  };

  const clearSearch = () => {
    setSearchValue('');
    performSearch('');
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (matches.length > 0) {
        navigateMatch('next');
      } else {
        performSearch(searchValue);
      }
    } else if (e.key === 'Escape') {
      clearSearch();
    }
  };

  const load = async () => {
    let apiJson;
    try {
      apiJson = new CsDiagnostics(csDiagnosticsParams).toApiJson();
    } catch (e) {
      toast.error(typeof e === 'string' ? e : e?.message || 'Validation failed');
      return;
    }
    try {
      setIsLoading(true);
      const response = await postSummaryEndpoint(endpoint, apiJson);
      const data = response?.data?.data;
      if (data && data.length > 0) {
        setRows(data);
        const rawKeys = Array.from(new Set(data.flatMap((obj) => Object.keys(obj))));
        setHeaders(resolveHeaders(rawKeys));
      } else {
        toast.warning(emptyWarning);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Something went wrong !');
    } finally {
      setIsLoading(false);
    }
  };

  const rowCountLabel = filteredData.length > MAX_VISIBLE_ROWS
    ? `${MAX_VISIBLE_ROWS} of ${filteredData.length} rows`
    : `${filteredData.length} rows`;

  const headerActions = (
    <div className="d-flex align-items-center gap-2 cs-panel__toolbar">
      <span className="cs-row-count">{rowCountLabel}</span>
      <div className="cs-global-search-container">
        <input
          id={`${sectionId}-global-search`}
          placeholder="Search"
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={onSearchKeyDown}
          value={searchValue}
          className="s-round-input"
          type="text"
          aria-label={`${title} global search`}
        />
        {searchValue && (
          <div className="cs-search-nav">
            <span className="cs-search-count">
              {matches.length > 0 ? `${currentMatchIndex + 1}/${matches.length}` : '0/0'}
            </span>
            <button
              type="button"
              className="cs-search-arrow"
              onClick={() => navigateMatch('prev')}
              disabled={matches.length === 0}
              title="Previous match"
            >
              <FontAwesomeIcon icon={faChevronUp} />
            </button>
            <button
              type="button"
              className="cs-search-arrow"
              onClick={() => navigateMatch('next')}
              disabled={matches.length === 0}
              title="Next match"
            >
              <FontAwesomeIcon icon={faChevronDown} />
            </button>
            <button
              type="button"
              className="cs-search-clear"
              onClick={clearSearch}
              title="Clear search"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
      </div>
      <button type="button" className="btn btn-sm btn-voltup-theme" onClick={() => load()}>
        Load
      </button>
      {getExportFilename || exportTableKey || exportSlug ? (
        <TableExportButtons
          canExport={canExport}
          isExporting={isExporting}
          onCsv={exportCsv}
          onExcel={exportExcel}
          getResolvedExportFilename={getResolvedExportFilename}
          getExportScopeCounts={resolveScopeCounts}
        />
      ) : null}
    </div>
  );

  return (
    <ExpandablePanel title={title} fixedHeight={fixedHeight} headerActions={headerActions}>
      <table
        id={sectionId}
        className={`table align-top table-sm cs-table${resolveStickyColumnKey ? ' cs-table--sticky-layout' : ''}`}
      >
        <thead className="table-light">
          <tr>
            {headers.map((key) => (
              <th
                key={key}
                scope="col"
                className={isStickyColumn(key) ? 'cs-table__sticky-col' : undefined}
                style={{ minWidth: '100px' }}
              >
                {beautifyColumnKey(key)}
              </th>
            ))}
          </tr>
          <tr>
            {headers.map((key) => (
              <th key={`f-${key}`} className={isStickyColumn(key) ? 'cs-table__sticky-col' : undefined}>
                <input
                  type="text"
                  className="form-control form-control-sm font-size-12"
                  placeholder={`Search ${key}`}
                  onChange={(e) => handleSearchChange(e, key)}
                  value={inputValues[key] || ''}
                />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.length > 0 ? (
            visibleRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((key, colIndex) => (
                  <td
                    key={key}
                    className={isStickyColumn(key) ? 'cs-table__sticky-col' : undefined}
                    ref={(el) => {
                      cellRefs.current[`${sectionId}-${rowIndex}-${colIndex}`] = el;
                    }}
                    style={{ maxWidth: '350px', whiteSpace: 'normal', wordWrap: 'break-word' }}
                  >
                    {row[key] || '- -'}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Math.max(headers.length, 1)} className="text-center py-4">
                No matching records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ExpandablePanel>
  );
}
